/**
 * GameLogSheet — the game log grown to just under the header bar, so more of
 * it shows at once (the log controls' grow button). It's an EdgeSheet like the
 * cards modal: portrait keeps the top bar showing, the wide layouts keep their
 * side column, and it slides up over the map.
 *
 * It carries the same chat bar as the in-place log (controls, filters, input)
 * above the list: the whole log (ALL) or chat only (CHAT), with the shared
 * filters. Tapping an event slides the sheet back down and then opens the
 * snapshot at that event (onViewAtEvent runs once the sheet has closed), so
 * the map is back in view for it.
 */
import React from 'react'
import { View } from 'react-native'
import type { ChatMessageResponse, GamePlayerResponse } from '@warcarp/api-types'
import type { GameState } from '../../lib/gameState'
import type { ActivityFilterState } from '../../lib/activityFilters'
import { EdgeSheet } from '../ui/EdgeSheet'
import { RecentActivitySidebar } from './RecentActivitySidebar'
import { ChatLogList } from './GameChatPanel'
import type { GameLogView } from './GameLogControls'

export function GameLogSheet({
  visible,
  onClose,
  keepTopVisible,
  keepLeftVisible,
  chatBar,
  view,
  gameId,
  state,
  players,
  messages,
  filters,
  onViewAtEvent,
  selectedEventIndex,
  eventIndexOffset,
}: {
  visible: boolean
  onClose: () => void
  keepTopVisible: number | null
  keepLeftVisible: number | null
  /** The chat bar (log controls, filters, input). */
  chatBar: React.ReactNode
  view: GameLogView
  gameId: string
  state: GameState
  players: GamePlayerResponse[]
  messages: ChatMessageResponse[]
  filters: ActivityFilterState
  /** Opens the snapshot; called after the sheet has slid away. */
  onViewAtEvent?: (eventIndex: number) => void
  selectedEventIndex?: number | null
  eventIndexOffset?: number
}) {
  // The event tapped in the sheet: opened once the exit slide has finished.
  const pendingEvent = React.useRef<number | null>(null)
  const viewAtEvent = React.useCallback((eventIndex: number) => {
    pendingEvent.current = eventIndex
    onClose()
  }, [onClose])
  const onClosed = React.useCallback(() => {
    const eventIndex = pendingEvent.current
    pendingEvent.current = null
    if (eventIndex !== null) onViewAtEvent?.(eventIndex)
  }, [onViewAtEvent])

  return (
    <EdgeSheet
      visible={visible}
      onRequestClose={onClose}
      onClosed={onClosed}
      closeAccessibilityLabel="Shrink the log"
      testIDPrefix="game-log-sheet"
      keepTopVisible={keepTopVisible}
      keepLeftVisible={keepLeftVisible}
    >
      {chatBar}
      {/* Its own id: the in-place log behind the modal holds the same rows. */}
      <View testID="game-log-sheet-log" collapsable={false} style={{ flex: 1 }}>
      {view === 'chat' ? (
        <ChatLogList messages={messages} state={state} players={players} orientation="bottom-panel" filters={filters} />
      ) : (
        <RecentActivitySidebar
          gameId={gameId}
          state={state}
          players={players}
          orientation="bottom-panel"
          onViewAtEvent={onViewAtEvent ? viewAtEvent : undefined}
          hideHeader
          selectedEventIndex={selectedEventIndex}
          eventIndexOffset={eventIndexOffset}
          infiniteScroll
          filters={filters}
        />
      )}
      </View>
    </EdgeSheet>
  )
}
